import { useMemo, useState } from "react";
import { useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { MessageCircle, FileSignature, BusFront, CalendarCheck, Landmark, CalendarDays, Loader2, ChevronRight, GraduationCap , Sun, CreditCard, ShieldCheck } from "lucide-react";
import { StudentToday } from "@/components/parent/StudentToday";
import { StudentTuition } from "@/components/parent/StudentTuition";
import { StudentMessages } from "@/components/parent/StudentMessages";
import { StudentForms } from "@/components/parent/StudentForms";
import { StudentBus } from "@/components/parent/StudentBus";
import { StudentAttendance } from "@/components/parent/StudentAttendance";
import { StudentGrades } from "@/components/parent/StudentGrades";
import { StudentHeader } from "@/components/parent/StudentHeader";
import { viewFromApi } from "@/components/FileViewer";
import parentApi from "@/lib/parentApi";

function QuickLink({ icon: Icon, label, sub, onClick, badge, testid, tone }) {
  return (
    <button onClick={onClick} data-testid={testid} className="flex items-center gap-3 rounded-2xl bg-white p-3.5 shadow-sm text-left hover:shadow transition active:scale-[0.99] min-w-0">
      <span className={`h-9 w-9 shrink-0 rounded-xl inline-flex items-center justify-center ${tone}`}><Icon className="h-4 w-4" /></span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-slate-900 truncate">{label}</span>
        <span className="block text-[11px] text-slate-500 truncate">{sub}</span>
      </span>
      {badge > 0 && <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 text-[11px] font-bold text-white">{badge}</span>}
      <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />
    </button>
  );
}

export default function ParentStudentPage() {
  const { studentId } = useParams();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const { schools } = useOutletContext();
  const [busy] = useState(false);

  const { school, student } = useMemo(() => {
    for (const sc of schools || []) {
      const st = sc.students.find((s) => s.id === studentId);
      if (st) return { school: sc, student: st };
    }
    return {};
  }, [schools, studentId]);

  const isDaycare = school?.company_type === "daycare";
  const tabs = [
    ...(isDaycare ? [{ key: "today", label: "Today", icon: Sun }, { key: "tuition", label: "Tuition", icon: CreditCard }] : []),
    { key: "messages", label: "Messages", icon: MessageCircle, badge: student?.unread },
    { key: "forms", label: "Forms", icon: FileSignature, badge: student?.pending_forms },
    ...(student?.is_bus_rider ? [{ key: "bus", label: "Bus", icon: BusFront }] : []),
    { key: "attendance", label: "Attendance", icon: CalendarCheck },
    ...(isDaycare ? [] : [{ key: "grades", label: "Grades", icon: GraduationCap }]),
  ];
  const tab = tabs.some((t) => t.key === params.get("tab")) ? params.get("tab") : (isDaycare ? "today" : "messages");

  if (schools === null) return <div className="flex-1 flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (!student) return <div className="p-8 text-center text-sm text-slate-500" data-testid="parent-student-missing">This student isn't linked to your account.</div>;

  return (
    <div className="flex-1 flex flex-col min-h-0" data-testid="parent-student-page">
      <header className="px-6 pt-2 pb-4">
        <div className="rounded-2xl bg-white border border-slate-200 shadow-sm px-5 pt-5 pb-0 md:pb-5">
          <StudentHeader school={school} student={student} section={tabs.find((t) => t.key === tab)?.label} />
          <div className="mt-3 flex gap-1 overflow-x-auto -mb-px md:hidden" data-testid="parent-student-tabs">
            {tabs.map((t) => (
              <button key={t.key} onClick={() => setParams({ tab: t.key })} data-testid={`parent-tab-${t.key}`}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition ${tab === t.key ? "border-primary text-primary" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
                <t.icon className="h-4 w-4" /> {t.label}
                {t.badge > 0 && <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">{t.badge}</span>}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="flex-1 min-h-0 flex flex-col w-full px-6 pb-6 gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:hidden" data-testid="parent-school-links">
          <QuickLink icon={Landmark} label="Contact office" sub={`${school.name} administration`} badge={school.office_unread} tone="bg-emerald-100 text-emerald-700"
            onClick={() => nav(`/parent/office/${school.company_id}?student=${student.id}`)} testid="parent-office-link" />
          <QuickLink icon={CalendarDays} label="School calendar" sub="Holidays, breaks & work days" tone="bg-rose-100 text-rose-600"
            onClick={() => nav(`/parent/calendar?school=${school.company_id}&student=${student.id}`)} testid="parent-calendar-link" />
        </div>
        <div className="flex justify-end -mt-1">
          <button onClick={() => viewFromApi(parentApi, `/parent/students/${student.id}/record.pdf`, { title: `${student.full_name} — full record`, filename: `record-${student.id}.pdf`, type: "application/pdf" })} data-testid="parent-download-record"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-primary">
            <ShieldCheck className="h-3.5 w-3.5" /> Download {student.full_name.split(" ")[0]}'s full record (PDF)
          </button>
        </div>
        <div className={`flex-1 min-h-0 flex flex-col ${busy ? "opacity-60" : ""}`}>
          {tab === "today" && <StudentToday studentId={student.id} />}
          {tab === "tuition" && <StudentTuition studentId={student.id} />}
          {tab === "messages" && <StudentMessages studentId={student.id} />}
          {tab === "forms" && <StudentForms studentId={student.id} />}
          {tab === "bus" && <StudentBus studentId={student.id} />}
          {tab === "attendance" && <StudentAttendance studentId={student.id} />}
          {tab === "grades" && <StudentGrades studentId={student.id} />}
        </div>
      </main>
    </div>
  );
}
